import { $generateHtmlFromNodes } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { ContentPreviewStyled } from 'components/editor/RichTextEditor.styles';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { RichTextEditorProps } from '../../RichTextEditor.types';
import useFetchPreviewContent from './useFetchPreviewContent';

interface PreviewPluginProps {
  toolbarSettings: RichTextEditorProps['toolbarSettings'];
}

const PreviewPlugin = ({ toolbarSettings }: PreviewPluginProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();

  const content = editor
    .getEditorState()
    .read(() => $generateHtmlFromNodes(editor));

  const { data, isLoading } = useFetchPreviewContent({
    content,
    toolbarSettings,
  });

  if (!toolbarSettings?.enabled || !toolbarSettings.features?.preview) {
    return null;
  }

  if (isLoading) {
    return <LoadingPlaceholder linesCount={10.71} />;
  }

  if (!data) {
    return (
      <ContentPreviewStyled data-testid="rich-text-editor-preview-area">
        {t('form.editor.preview.errorMessage')}
      </ContentPreviewStyled>
    );
  }

  const sanitizedHtmlContent = DOMPurify.sanitize(data);

  return (
    <ContentPreviewStyled
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizedHtmlContent }}
      data-testid="rich-text-editor-preview-area"
    />
  );
};

export default PreviewPlugin;
